import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { key: 2 }

import { reactive, ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { v4 as uuidv4 } from 'uuid';
import { getFileDownloadUrl } from '@/utils/fileService';
import {
  Attributes_BclToFastq,
  Attributes_BclToFastq_Sample,
} from '@/utils/compute-utils';
import { ComputeTaskCreateDocument } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue-next';
import { COMPUTE_TASK_TYPES } from '@/constants';
import CloudRegionSelection from '@/components/compute/CloudRegionSelection.vue';
import { useUserStore } from '@/store/user';
import DeveloperOptions from '@/components/compute/DeveloperOptions.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'BclToFastqNew',
  props: {
    show: { type: Boolean },
    bucketId: {},
    bucketName: {},
    fileBcl: {},
    fileCsv: {},
    defaultName: {}
  },
  emits: ['close', 'closeAndRefresh'],
  setup(__props: any, { emit: __emit }) {

const userStore = useUserStore();

// --- Component references ---
const devOptions = ref<InstanceType<typeof DeveloperOptions>>();

// --- Component properties ---
const props = __props;

const emits = __emit;

// --- Component state ---
const computeName = ref<string>(props.defaultName);
const customCloudRegionPrefix = ref<string | null>(null);
const customCloudRegionRequired = ref(false);

const csvFileContent = ref<string>('');
const csvFileLoading = ref<boolean>(false);
const csvFileLoadingError = ref<string | null>(null);

const show = computed<boolean>(() => {
  return props.show;
});

// --- sample sheet rows ---
const rows = reactive<Attributes_BclToFastq_Sample[]>([]);

async function initializeRows() {
  rows.splice(0); // clear rows
  if (props.fileCsv) {
    await fetchSampleSheetCsv();
    await parseSampleSheetCsv();
  } else {
    rows.push({ id: '', name: '', index: '', index2: '' });
  }
}
initializeRows();

function addRow() {
  rows.push({ id: '', name: '', index: '', index2: '' });
}
function removeRow(idx: number) {
  rows.splice(idx, 1);
}

function loadSampleSheetCsvFromFile(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files.length === 1) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        csvFileContent.value = e.target?.result as string;
        parseSampleSheetCsv();
      };
      reader.readAsText(file);
    }
  }
}

async function fetchSampleSheetCsv() {
  if (!props.fileCsv) {
    csvFileLoadingError.value = 'No CSV file provided';
    return;
  }

  // Set loading state and clear the content
  csvFileContent.value = '';
  csvFileLoading.value = true;

  // Attempt to fetch the CSV file
  try {
    // Get download URL
    const downloadUrl = await getFileDownloadUrl(props.bucketId, props.fileCsv);
    // Validate URL
    if (!downloadUrl) {
      console.error(
        `Unexpected error fetching download URL for the CSV file ${props.fileCsv}`
      );
      alert(
        'Failed to generate download link. The requested CSV file might not be available anymore.'
      );
      return;
    }
    // Fetch content if preview is embedded
    const response = await fetch(downloadUrl);
    // Validate response
    if (!response.ok) {
      throw new Error('Failed to fetch CSV file content.');
    }
    // Plain text content
    csvFileContent.value = await response.text();
    // Otherwise clear the error
    csvFileLoadingError.value = null;
    console.debug('CSV file content loaded:', csvFileContent.value);
  } catch (err: any) {
    csvFileLoadingError.value = err.message;
  } finally {
    csvFileLoading.value = false;
  }
}

async function parseSampleSheetCsv() {
  if (!csvFileContent.value) {
    csvFileLoadingError.value = 'No CSV file content to parse';
    return;
  }
  csvFileLoading.value = true;
  try {
    // Remove lines before the line starting with [Data], including the line itself and the next line
    // This is because the CSV file may contain metadata or comments before the actual data section
    // The first line afar the [Data] line is the header
    const lines = csvFileContent.value.split('\n');
    const dataStartIndex = lines.findIndex((line) => line.startsWith('[Data]'));
    if (dataStartIndex === -1) {
      throw new Error('No [Data] section found in the provided CSV file.');
    }
    // Ensure there is enough lines after the header
    if (dataStartIndex + 2 >= lines.length) {
      throw new Error('No data found in the provided CSV file.');
    }
    // Extract the data lines after the header
    const dataLines = lines.slice(dataStartIndex + 2);
    // Parse the CSV data
    const parsedData = dataLines
      .map((line) => line.split(','))
      .filter((line) => line.length >= 4)
      .map((line) => ({
        id: line[0].trim(),
        name: line[1].trim(),
        index: line[2].trim(),
        index2: line[3].trim(),
      }));
    // Clear existing rows and add parsed data
    rows.splice(0); // clear rows
    parsedData.forEach((row) => {
      rows.push({
        id: row.id,
        name: row.name,
        index: row.index,
        index2: row.index2,
      });
    });
    // Check if the CSV file is empty
    if (rows.length === 0) {
      throw new Error('CSV file is empty or invalid.');
    }
    // Otherwise clear the error
    csvFileLoadingError.value = null;
    console.debug('CSV file parsed successfully:', rows);
  } catch (err: any) {
    csvFileLoadingError.value = err.message;
  } finally {
    if (rows.length === 0) {
      addRow();
    }
    csvFileLoading.value = false;
  }
}

const sampleMatrixFields = [
  {
    key: 'id',
    label: 'Sample ID',
    tdClass: 'align-middle',
    sortable: true,
  },
  {
    key: 'name',
    label: 'Sample Name',
    sortable: true,
    tdClass: 'align-middle',
  },
  { key: 'index', label: 'i7 Index', sortable: true, tdClass: 'align-middle' },
  {
    key: 'index2',
    label: 'Reverse i5 Index',
    sortable: true,
    tdClass: 'align-middle',
  },
  { key: 'actions', label: '', tdClass: 'align-middle text-end min-width' },
];

// --- ComputeTaskCreate Mutation ---
const mutationError = ref<string | null>(null);
const mutationInProgress = ref<boolean>(false);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(ComputeTaskCreateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.computeTaskCreate) {
    console.debug(
      'BCL to FASTQ conversion started:',
      res.data.computeTaskCreate
    );
    mutationShowSuccess.value = true;
    setTimeout(() => {
      emits('closeAndRefresh');
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    mutationError.value = 'BCL to FASTQ conversion start failed: ' + res.data;
    console.error('BCL to FASTQ conversion start failed:', res.data);
  }
});

onMutationError((err) => {
  console.error('Error starting BCL to FASTQ conversion:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function mutationRequested() {
  if (devOptions.value?.extraAttributesInvalid) {
    mutationError.value =
      'Developer JSON attributes must be a valid JSON object.';
    return;
  }

  // check rows do not contain any non-alphanumeric characters (incl spaces)
  for (const row of rows) {
    if (!/^[a-zA-Z0-9-]+$/.test(row.id)) {
      mutationError.value =
        'Sample ID contains invalid characters. Use alphanumeric characters and dashes only.';
      console.error('Sample ID contains invalid characters');
      return;
    }
    if (!/^[a-zA-Z0-9-]+$/.test(row.name)) {
      mutationError.value =
        'Sample name contains invalid characters.  Use alphanumeric characters and dashes only.';
      console.error('Sample name contains invalid characters');
      return;
    }
  }

  // initialize progress values
  mutationInProgress.value = true;
  mutationError.value = null;
  mutationShowSuccess.value = false;

  // initialize attributes
  let attributes: Attributes_BclToFastq = {
    gceRequestId: uuidv4(),
    bucketId: props.bucketId,
    bucketName: props.bucketName,
    fileBcl: props.fileBcl,
    samples: rows,
  };

  // add developer options
  if (userStore.developer && devOptions.value?.expanded) {
    // general - version
    if (devOptions.value.version) {
      attributes.version = devOptions.value.version;
    }

    // general - vm lifecycle
    if (devOptions.value.vmLifecycle) {
      attributes.vmLifecycle = devOptions.value.vmLifecycle;
    }

    // general - machine type
    if (devOptions.value.machineType) {
      attributes.machineType = devOptions.value.machineType;
    }

    // extra attributes from JSON
    if (devOptions.value.extraAttributes) {
      Object.assign(attributes, devOptions.value.extraAttributes);
    }
  }

  console.debug(
    'New BCL to FASTQ conversion requested with attributes:',
    attributes
  );

  // mutationInProgress.value = false;
  executeMutation({
    name: computeName.value,
    bucketId: props.bucketId,
    taskType: COMPUTE_TASK_TYPES.BCL_TO_FASTQ,
    attributes: attributes,
    requestedRegionPrefix: customCloudRegionPrefix.value
      ? customCloudRegionPrefix.value
      : undefined,
  });
}

function onModalHide(evt: Event) {
  // prevent closing modal if mutation is in progress
  if (mutationInProgress.value) {
    evt.preventDefault();
    return;
  }
  // otherwise
  emits('close');
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BModal), {
      modelValue: show.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((show).value = $event)),
      onHide: onModalHide,
      backdrop: mutationInProgress.value ? 'static' : true,
      "no-close-on-esc": mutationInProgress.value,
      class: "modal-lg"
    }, {
      title: _withCtx(() => [
        _createVNode(_component_font_awesome_icon, {
          icon: "shuffle",
          class: "me-2"
        }),
        _cache[8] || (_cache[8] = _createTextVNode(" Start new BCL to FASTQ conversion ", -1))
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(BButton), {
          variant: "secondary",
          onClick: onModalHide,
          disabled: mutationInProgress.value
        }, {
          default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
            _createTextVNode(" Cancel ", -1)
          ]))]),
          _: 1
        }, 8, ["disabled"]),
        _createVNode(_unref(BButton), {
          variant: "primary",
          onClick: mutationRequested,
          disabled: mutationInProgress.value || mutationShowSuccess.value
        }, {
          default: _withCtx(() => [
            (mutationInProgress.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
                  _cache[12] || (_cache[12] = _createTextVNode(" Starting Conversion ", -1)),
                  _createVNode(_unref(BSpinner), {
                    small: "",
                    label: ""
                  })
                ]))
              : (mutationShowSuccess.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_2, [
                    _cache[13] || (_cache[13] = _createTextVNode(" Conversion Started ", -1)),
                    _createVNode(_component_font_awesome_icon, {
                      icon: ['fa', 'check-double'],
                      class: "me-2"
                    })
                  ]))
                : (_openBlock(), _createElementBlock("span", _hoisted_3, " Start Conversion "))
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: computeName.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((computeName).value = $event)),
                id: "task-name",
                name: "task-name",
                placeholder: "Enter conversion task name",
                required: ""
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
            default: _withCtx(() => [
              _createVNode(_unref(BTable), {
                items: rows,
                fields: sampleMatrixFields,
                busy: csvFileLoading.value,
                small: "",
                responsive: "",
                class: "browser-table"
              }, {
                "cell(id)": _withCtx((data) => [
                  _createVNode(_unref(BFormInput), {
                    modelValue: rows[data.index].id,
                    "onUpdate:modelValue": ($event: any) => ((rows[data.index].id) = $event),
                    size: "sm"
                  }, null, 8, ["modelValue", "onUpdate:modelValue"])
                ]),
                "cell(name)": _withCtx((data) => [
                  _createVNode(_unref(BFormInput), {
                    modelValue: rows[data.index].name,
                    "onUpdate:modelValue": ($event: any) => ((rows[data.index].name) = $event),
                    size: "sm"
                  }, null, 8, ["modelValue", "onUpdate:modelValue"])
                ]),
                "cell(index)": _withCtx((data) => [
                  _createVNode(_unref(BFormInput), {
                    modelValue: rows[data.index].index,
                    "onUpdate:modelValue": ($event: any) => ((rows[data.index].index) = $event),
                    size: "sm"
                  }, null, 8, ["modelValue", "onUpdate:modelValue"])
                ]),
                "cell(index2)": _withCtx((data) => [
                  _createVNode(_unref(BFormInput), {
                    modelValue: rows[data.index].index2,
                    "onUpdate:modelValue": ($event: any) => ((rows[data.index].index2) = $event),
                    size: "sm"
                  }, null, 8, ["modelValue", "onUpdate:modelValue"])
                ]),
                "cell(actions)": _withCtx((data) => [
                  _createVNode(_unref(BButton), {
                    disabled: rows.length <= 1,
                    variant: "outline-danger",
                    size: "sm",
                    onClick: ($event: any) => (removeRow(data.index)),
                    title: "Remove sample"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_font_awesome_icon, { icon: "trash" })
                    ]),
                    _: 1
                  }, 8, ["disabled", "onClick"])
                ]),
                _: 1
              }, 8, ["items", "busy"]),
              _createVNode(_unref(BButton), {
                variant: "outline-primary",
                size: "sm",
                onClick: addRow,
                class: "me-2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_font_awesome_icon, {
                    icon: "plus",
                    class: "me-2"
                  }),
                  _cache[9] || (_cache[9] = _createTextVNode(" Add sample ", -1))
                ]),
                _: 1
              }),
              _createVNode(_unref(BButton), {
                tag: "label",
                variant: "outline-primary",
                size: "sm",
                class: "me-2"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_font_awesome_icon, {
                    icon: "file-csv",
                    class: "me-2"
                  }),
                  _cache[10] || (_cache[10] = _createTextVNode(" Load samples from CSV ", -1)),
                  _createVNode(_unref(BFormInput), {
                    type: "file",
                    accept: ".csv",
                    ref: "fileInput",
                    onChange: loadSampleSheetCsvFromFile,
                    class: "d-none"
                  }, null, 512)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(BAlert), {
            modelValue: csvFileLoadingError.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((csvFileLoadingError).value = $event)),
            variant: "danger",
            dismissible: "",
            class: "mt-3",
            onDismissed: _cache[2] || (_cache[2] = ($event: any) => (csvFileLoadingError.value = null))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(csvFileLoadingError.value), 1)
            ]),
            _: 1
          }, 8, ["modelValue"]),
          (_unref(userStore).developer)
            ? (_openBlock(), _createBlock(DeveloperOptions, {
                key: 0,
                ref_key: "devOptions",
                ref: devOptions,
                size: "sm",
                class: "mt-3"
              }, null, 512))
            : _createCommentVNode("", true),
          _createVNode(CloudRegionSelection, {
            modelValue: customCloudRegionPrefix.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((customCloudRegionPrefix).value = $event)),
            modelRequired: customCloudRegionRequired.value,
            "onUpdate:modelRequired": _cache[4] || (_cache[4] = ($event: any) => ((customCloudRegionRequired).value = $event)),
            class: "mt-3"
          }, null, 8, ["modelValue", "modelRequired"]),
          _createVNode(_unref(BAlert), {
            modelValue: mutationError.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((mutationError).value = $event)),
            variant: "danger",
            dismissible: "",
            class: "mt-3",
            onDismissed: _cache[6] || (_cache[6] = ($event: any) => (mutationError.value = null))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(mutationError.value), 1)
            ]),
            _: 1
          }, 8, ["modelValue"])
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "backdrop", "no-close-on-esc"])
  ]))
}
}

})